import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { containerService } from '../../services/containerService';
import { realtimeService } from '../../services/realtimeService';
import { nextDepartureService } from '../../services/nextDepartureService';
import { getStatusLabel } from '../../services/containerService';
import { trackContainerEvent, trackSearch, trackNavigation, trackFormSubmit, trackError } from '../../services/analyticsService';

export default function AdminContainersDashboard() {
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Add these missing state declarations
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [userRole, setUserRole] = useState('admin');
  
  // Add state for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Add this missing state declaration
  const [showArchived, setShowArchived] = useState(false);

  // Add these missing state declarations for Archive Modal
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Edit container state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContainer, setEditingContainer] = useState(null);
  const [editFormData, setEditFormData] = useState({
    containerCode: '',
    departurePort: '',
    destinationPort: '',
    departureDate: '',
    etaDate: '',
    shippingLine: '',
    vesselName: '',
    status: 'AU_PORT_LE_HAVRE_ANVERS',
    notes: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Form state for creating container
  const [formData, setFormData] = useState({
    containerCode: '',
    departurePort: '',
    destinationPort: '',
    departureDate: '',
    etaDate: '',
    shippingLine: '',
    vesselName: '',
    status: 'AU_PORT_LE_HAVRE_ANVERS',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  // Add these missing state declarations for Next Departure Modal
  const [showNextDepartureModal, setShowNextDepartureModal] = useState(false);
  const [nextDepartureData, setNextDepartureData] = useState({
    departureDate: '',
    vesselName: '',
    estimatedArrival: '',
    port: ''
  });
  const [nextDepartureErrors, setNextDepartureErrors] = useState({});
  const [isSavingDeparture, setIsSavingDeparture] = useState(false);

  // Subscribe to real-time containers updates
  useEffect(() => {
    if (!isAuthenticated || userRole !== 'admin') return;

    const subscription = realtimeService?.subscribeToContainers((payload) => {
      if (payload?.eventType === 'INSERT') {
        setContainers(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setContainers(prev => prev?.map(container =>
          container?.id === payload?.new?.id ? payload?.new : container
        ));
      } else if (payload?.eventType === 'DELETE') {
        setContainers(prev => prev?.filter(container => container?.id !== payload?.old?.id));
      }
    });

    return () => {
      realtimeService?.unsubscribe('containers-realtime');
    };
  }, [isAuthenticated, userRole]);

  useEffect(() => {
    loadContainers();
  }, [searchTerm, statusFilter, showArchived]);

  const loadContainers = async () => {
    try {
      setLoading(true);
      const data = await containerService?.getAll({
        searchTerm,
        status: statusFilter,
        showArchived
      });
      setContainers(data);
      setError('');
      
      // Track container search/filter
      if (searchTerm) {
        trackSearch('container_search', searchTerm, data?.length);
      }
      if (statusFilter) {
        trackSearch('container_filter_status', statusFilter, data?.length);
      }
    } catch (err) {
      setError('Erreur lors du chargement des conteneurs');
      console.error('Error loading containers:', err);
      trackError('container_load_error', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'AU_PORT_LE_HAVRE_ANVERS': 'bg-gray-100 text-gray-800',
      'EN_TRANSIT_MARITIME': 'bg-blue-100 text-blue-800',
      'ARRIVE_A_ABIDJAN': 'bg-purple-100 text-purple-800',
      'DEDOUANEMENT_EN_COURS': 'bg-yellow-100 text-yellow-800',
      'TRANSFERT_A_ENTREPOT': 'bg-orange-100 text-orange-800',
      'DISTRIBUTION_EN_COURS': 'bg-indigo-100 text-indigo-800',
      'CONTENEUR_TERMINE': 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleViewDetails = (containerId) => {
    trackNavigation('Container Detail View', { containerId });
    navigate(`/admin-container-detail/${containerId}`);
  };

  // Form handlers
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (formErrors?.[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.containerCode?.trim()) {
      errors.containerCode = 'Code conteneur requis';
    }
    if (!formData?.departurePort?.trim()) {
      errors.departurePort = 'Port de départ requis';
    }
    if (!formData?.destinationPort?.trim()) {
      errors.destinationPort = "Port d'arrivée requis";
    }
    if (!formData?.departureDate) {
      errors.departureDate = 'Date de départ requise';
    }
    
    return errors;
  };

  const handleCreateContainer = async (e) => {
    e?.preventDefault();
    
    trackFormSubmit('create_container', { status: formData?.status });
    
    const errors = validateForm();
    if (Object.keys(errors)?.length > 0) {
      setFormErrors(errors);
      trackError('validation_error', 'Container form validation failed', errors);
      return;
    }

    setIsCreating(true);

    try {
      const newContainer = await containerService?.create(formData);
      
      // Track successful container creation
      trackContainerEvent('created', {
        id: newContainer?.id,
        reference: newContainer?.container_code,
        status: newContainer?.status
      });
      
      setShowCreateModal(false);
      resetForm();
      await loadContainers();
    } catch (err) {
      setFormErrors({ submit: err?.message });
      trackError('container_creation_error', err?.message, { container_code: formData?.containerCode });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setFormData({
      containerCode: '',
      departurePort: '',
      destinationPort: '',
      departureDate: '',
      etaDate: '',
      shippingLine: '',
      vesselName: '',
      status: 'AU_PORT_LE_HAVRE_ANVERS',
      notes: ''
    });
    setFormErrors({});
    setShowCreateModal(false);
  };

  const resetForm = () => {
    setFormData({
      containerCode: '',
      departurePort: '',
      destinationPort: '',
      departureDate: '',
      etaDate: '',
      shippingLine: '',
      vesselName: '',
      status: 'AU_PORT_LE_HAVRE_ANVERS',
      notes: ''
    });
    setFormErrors({});
  };

  // Add these missing handler functions for Next Departure Modal
  const handleNextDepartureChange = (field, value) => {
    setNextDepartureData(prev => ({ ...prev, [field]: value }));
    if (nextDepartureErrors?.[field]) {
      setNextDepartureErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateNextDeparture = () => {
    const errors = {};
    if (!nextDepartureData?.departureDate?.trim()) {
      errors.departureDate = 'Date de départ requise';
    }
    if (!nextDepartureData?.vesselName?.trim()) {
      errors.vesselName = 'Nom du navire requis';
    }
    if (!nextDepartureData?.estimatedArrival?.trim()) {
      errors.estimatedArrival = 'Date d\'arrivée requise';
    }
    return errors;
  };

  const handleSaveNextDeparture = async (e) => {
    e?.preventDefault();
    
    const errors = validateNextDeparture();
    if (Object.keys(errors)?.length > 0) {
      setNextDepartureErrors(errors);
      trackError('validation_error', 'Next departure form validation failed', errors);
      return;
    }

    setIsSavingDeparture(true);

    try {
      await nextDepartureService?.upsert(nextDepartureData);
      
      trackFormSubmit('save_next_departure', nextDepartureData);
      setShowNextDepartureModal(false);
      setNextDepartureData({
        departureDate: '',
        vesselName: '',
        estimatedArrival: '',
        port: ''
      });
      setNextDepartureErrors({});
      
      // Show success message
      alert('Informations du prochain départ enregistrées avec succès!');
    } catch (err) {
      setNextDepartureErrors({ submit: err?.message });
      trackError('next_departure_save_error', err?.message);
    } finally {
      setIsSavingDeparture(false);
    }
  };

  const handleCancelNextDeparture = () => {
    setNextDepartureData({
      departureDate: '',
      vesselName: '',
      estimatedArrival: '',
      port: ''
    });
    setNextDepartureErrors({});
    setShowNextDepartureModal(false);
  };

  // Add handlers for delete and archive
  const handleDeleteClick = (e, container) => {
    e?.stopPropagation();
    setSelectedContainer(container);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedContainer) return;
    
    setIsDeleting(true);
    try {
      await containerService?.delete(selectedContainer?.id);
      trackContainerEvent('deleted', {
        id: selectedContainer?.id,
        reference: selectedContainer?.containerCode
      });
      setShowDeleteConfirm(false);
      setSelectedContainer(null);
      await loadContainers();
    } catch (err) {
      alert(err?.message || 'Erreur lors de la suppression du conteneur');
      trackError('container_delete_error', err?.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedContainer(null);
  };

  // Edit container handlers
  const handleEditClick = (e, container) => {
    e?.stopPropagation();
    setEditingContainer(container);
    setEditFormData({
      containerCode: container?.containerCode || '',
      departurePort: container?.departurePort || '',
      destinationPort: container?.destinationPort || '',
      departureDate: container?.departureDate || '',
      etaDate: container?.etaDate || '',
      shippingLine: container?.shippingLine || '',
      vesselName: container?.vesselName || '',
      status: container?.status || 'AU_PORT_LE_HAVRE_ANVERS',
      notes: container?.notes || ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    if (editFormErrors?.[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEditForm = () => {
    const errors = {};
    if (!editFormData?.containerCode?.trim()) errors.containerCode = 'Code conteneur requis';
    if (!editFormData?.departurePort?.trim()) errors.departurePort = 'Port de départ requis';
    if (!editFormData?.destinationPort?.trim()) errors.destinationPort = "Port d'arrivée requis";
    if (!editFormData?.departureDate) errors.departureDate = 'Date de départ requise';
    return errors;
  };

  const handleSaveEdit = async (e) => {
    e?.preventDefault();
    const errors = validateEditForm();
    if (Object.keys(errors)?.length > 0) {
      setEditFormErrors(errors);
      return;
    }
    setIsSavingEdit(true);
    try {
      await containerService?.update(editingContainer?.id, {
        containerCode: editFormData?.containerCode,
        departurePort: editFormData?.departurePort,
        destinationPort: editFormData?.destinationPort,
        departureDate: editFormData?.departureDate || null,
        etaDate: editFormData?.etaDate || null,
        shippingLine: editFormData?.shippingLine || null,
        vesselName: editFormData?.vesselName || null,
        status: editFormData?.status,
        notes: editFormData?.notes || null
      });
      setShowEditModal(false);
      setEditingContainer(null);
      await loadContainers();
    } catch (err) {
      setEditFormErrors({ submit: err?.message || 'Erreur lors de la modification' });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingContainer(null);
    setEditFormErrors({});
  };

  // Add this missing handler function for archive confirmation
  const confirmArchive = async () => {
    if (!selectedContainer) return;
    
    setIsArchiving(true);
    try {
      await containerService?.toggleArchive(selectedContainer?.id);
      trackContainerEvent(selectedContainer?.isArchived ? 'unarchived' : 'archived', {
        id: selectedContainer?.id,
        reference: selectedContainer?.containerCode
      });
      setShowArchiveConfirm(false);
      setSelectedContainer(null);
      await loadContainers();
    } catch (err) {
      alert(err?.message || 'Erreur lors de l\'archivage du conteneur');
      trackError('container_archive_error', err?.message);
    } finally {
      setIsArchiving(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/admin-shipments-dashboard' },
    { label: 'Conteneurs', path: '/admin-containers-dashboard' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <BreadcrumbTrail customItems={breadcrumbItems} />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des conteneurs</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Suivi et gestion des conteneurs maritimes</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={() => setShowNextDepartureModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
            >
              <Icon name="Ship" size={18} />
              <span>Infos Prochain Conteneur</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors shadow-sm min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
            >
              <Icon name="Plus" size={18} />
              <span>Nouveau conteneur</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Rechercher
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                placeholder="Code conteneur, navire..."
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
              >
                <option value="">Tous les statuts</option>
                <option value="AU_PORT_LE_HAVRE_ANVERS">Au port Le Havre/Anvers</option>
                <option value="EN_TRANSIT_MARITIME">En transit maritime</option>
                <option value="ARRIVE_A_ABIDJAN">Arrivé à Abidjan</option>
                <option value="DEDOUANEMENT_EN_COURS">Dédouanement en cours</option>
                <option value="TRANSFERT_A_ENTREPOT">Transfert à entrepôt</option>
                <option value="DISTRIBUTION_EN_COURS">Distribution en cours</option>
                <option value="CONTENEUR_TERMINE">Conteneur terminé</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Affichage
              </label>
              <select
                value={showArchived ? 'archived' : 'active'}
                onChange={(e) => setShowArchived(e?.target?.value === 'archived')}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
              >
                <option value="active">Conteneurs actifs</option>
                <option value="archived">Conteneurs archivés</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8 sm:py-12">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-sm sm:text-base text-gray-600 mt-4">Chargement...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-red-800">{error}</p>
          </div>
        )}

        {/* Grid View (existing container cards) */}
        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {containers?.map((container) => (
              <div
                key={container?.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 hover:shadow-md hover:border-green-500 active:border-green-600 transition-all cursor-pointer"
                onClick={() => handleViewDetails(container?.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 break-all">
                      {container?.containerCode}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      {container?.shippingLine}
                    </p>
                  </div>
                  <span className={`px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(container?.status)}`}>
                    {getStatusLabel(container?.status)}
                  </span>
                </div>

                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Ship" size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600">Navire:</span>
                    <span className="text-gray-900 font-medium truncate">{container?.vesselName || '-'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600">Route:</span>
                    <span className="text-gray-900 font-medium truncate">
                      {container?.departurePort} → {container?.destinationPort}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600">ETA:</span>
                    <span className="text-gray-900 font-medium">
                      {container?.etaDate ? new Date(container?.etaDate)?.toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleViewDetails(container?.id);
                  }}
                  className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[44px] text-sm sm:text-base"
                >
                  <Icon name="Eye" size={18} />
                  <span>Voir les détails</span>
                </button>

                {/* Delete and Archive buttons */}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={(e) => handleEditClick(e, container)}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[44px] text-sm"
                  >
                    <Icon name="Pencil" size={16} />
                    <span>Modifier</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedContainer(container);
                      setShowArchiveConfirm(true);
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 active:bg-orange-800 transition-colors min-h-[44px] text-sm"
                  >
                    <Icon name="Archive" size={16} />
                    <span>{container?.is_archived ? 'Restaurer' : 'Archiver'}</span>
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(e, container)}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors min-h-[44px] text-sm"
                  >
                    <Icon name="Trash2" size={16} />
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && containers?.length === 0 && (
          <div className="text-center py-8 sm:py-12 bg-white rounded-lg shadow-sm">
            <Icon name="Package" size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-base sm:text-lg text-gray-600">Aucun conteneur trouvé</p>
          </div>
        )}
      </div>

      {/* Next Departure Info Modal */}
      {showNextDepartureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-lg p-2">
                  <Icon name="Ship" size={20} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Infos Prochain Conteneur</h3>
              </div>
              <button
                onClick={handleCancelNextDeparture}
                disabled={isSavingDeparture}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSaveNextDeparture} className="p-4 space-y-4">
              {/* Departure Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Date de départ <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={nextDepartureData?.departureDate}
                  onChange={(e) => handleNextDepartureChange('departureDate', e?.target?.value)}
                  placeholder="Ex: 15 Février 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {nextDepartureErrors?.departureDate && (
                  <p className="text-red-600 text-sm mt-1">{nextDepartureErrors?.departureDate}</p>
                )}
              </div>

              {/* Vessel Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Nom du navire <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={nextDepartureData?.vesselName}
                  onChange={(e) => handleNextDepartureChange('vesselName', e?.target?.value)}
                  placeholder="Ex: MSC MEDITERRANEAN"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {nextDepartureErrors?.vesselName && (
                  <p className="text-red-600 text-sm mt-1">{nextDepartureErrors?.vesselName}</p>
                )}
              </div>

              {/* Estimated Arrival */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Arrivée à Abidjan <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={nextDepartureData?.estimatedArrival}
                  onChange={(e) => handleNextDepartureChange('estimatedArrival', e?.target?.value)}
                  placeholder="Ex: 28 Février 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {nextDepartureErrors?.estimatedArrival && (
                  <p className="text-red-600 text-sm mt-1">{nextDepartureErrors?.estimatedArrival}</p>
                )}
              </div>

              {/* Port */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Trajet
                </label>
                <input
                  type="text"
                  value={nextDepartureData?.port}
                  onChange={(e) => handleNextDepartureChange('port', e?.target?.value)}
                  placeholder="Ex: Anvers → Abidjan"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Ces informations s'afficheront automatiquement sur la page publique "Informations d'expédition".
                </p>
              </div>

              {nextDepartureErrors?.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{nextDepartureErrors?.submit}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancelNextDeparture}
                  disabled={isSavingDeparture}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSavingDeparture}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isSavingDeparture ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Container Creation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 rounded-lg p-2">
                  <Icon name="Package" size={20} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Nouveau Conteneur</h3>
              </div>
              <button
                onClick={handleCancelCreate}
                disabled={isCreating}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleCreateContainer} className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Container Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Code conteneur <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData?.containerCode}
                    onChange={(e) => handleFormChange('containerCode', e?.target?.value)}
                    placeholder="Ex: CONT-2026-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {formErrors?.containerCode && (
                    <p className="text-red-600 text-sm mt-1">{formErrors?.containerCode}</p>
                  )}
                </div>

                {/* Departure Port */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Port de départ <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData?.departurePort}
                    onChange={(e) => handleFormChange('departurePort', e?.target?.value)}
                    placeholder="Ex: Le Havre"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {formErrors?.departurePort && (
                    <p className="text-red-600 text-sm mt-1">{formErrors?.departurePort}</p>
                  )}
                </div>

                {/* Destination Port */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Port d'arrivée <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData?.destinationPort}
                    onChange={(e) => handleFormChange('destinationPort', e?.target?.value)}
                    placeholder="Ex: Abidjan"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {formErrors?.destinationPort && (
                    <p className="text-red-600 text-sm mt-1">{formErrors?.destinationPort}</p>
                  )}
                </div>

                {/* Departure Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Date de départ <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData?.departureDate}
                    onChange={(e) => handleFormChange('departureDate', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {formErrors?.departureDate && (
                    <p className="text-red-600 text-sm mt-1">{formErrors?.departureDate}</p>
                  )}
                </div>

                {/* ETA Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Date d'arrivée estimée
                  </label>
                  <input
                    type="date"
                    value={formData?.etaDate}
                    onChange={(e) => handleFormChange('etaDate', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* Shipping Line */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Compagnie maritime
                  </label>
                  <input
                    type="text"
                    value={formData?.shippingLine}
                    onChange={(e) => handleFormChange('shippingLine', e?.target?.value)}
                    placeholder="Ex: MSC"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* Vessel Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom du navire
                  </label>
                  <input
                    type="text"
                    value={formData?.vesselName}
                    onChange={(e) => handleFormChange('vesselName', e?.target?.value)}
                    placeholder="Ex: MSC MEDITERRANEAN"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Statut
                  </label>
                  <select
                    value={formData?.status}
                    onChange={(e) => handleFormChange('status', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="AU_PORT_LE_HAVRE_ANVERS">Au port Le Havre / Anvers</option>
                    <option value="EN_TRANSIT_MARITIME">En mer</option>
                    <option value="ARRIVE_A_ABIDJAN">Arrivé au port d'Abidjan</option>
                    <option value="DEDOUANEMENT_EN_COURS">En phase de dédouanement</option>
                    <option value="TRANSFERT_A_ENTREPOT">À l'entrepôt / magasin</option>
                    <option value="DISTRIBUTION_EN_COURS">Distribution en cours</option>
                    <option value="CONTENEUR_TERMINE">Conteneur terminé</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notes
                </label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => handleFormChange('notes', e?.target?.value)}
                  placeholder="Notes additionnelles..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {formErrors?.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{formErrors?.submit}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancelCreate}
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Création...' : 'Créer le conteneur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <Icon name="AlertTriangle" size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le conteneur <strong>{selectedContainer?.containerCode}</strong> ?
              Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] disabled:opacity-50"
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`${selectedContainer?.isArchived ? 'bg-green-100' : 'bg-orange-100'} rounded-full p-3`}>
                <Icon name="Archive" size={24} className={selectedContainer?.isArchived ? 'text-green-600' : 'text-orange-600'} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedContainer?.isArchived ? 'Restaurer le conteneur' : 'Archiver le conteneur'}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              {selectedContainer?.isArchived 
                ? `Voulez-vous restaurer le conteneur ${selectedContainer?.containerCode} ? Il redeviendra visible dans la liste active.`
                : `Voulez-vous archiver le conteneur ${selectedContainer?.containerCode} ? Il sera masqué de la liste active mais pourra être restauré plus tard.`
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setSelectedContainer(null);
                }}
                disabled={isArchiving}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Annuler
              </button>
              <button
                onClick={confirmArchive}
                disabled={isArchiving}
                className={`flex-1 px-4 py-2.5 ${selectedContainer?.isArchived ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-lg transition-colors min-h-[44px] disabled:opacity-50`}
              >
                {isArchiving ? 'Traitement...' : (selectedContainer?.isArchived ? 'Restaurer' : 'Archiver')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Container Modal */}
      {showEditModal && editingContainer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-lg p-2">
                  <Icon name="Pencil" size={20} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Modifier le conteneur</h3>
              </div>
              <button
                onClick={handleCancelEdit}
                disabled={isSavingEdit}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="p-4 space-y-4">
              {editFormErrors?.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{editFormErrors?.submit}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Code conteneur <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData?.containerCode}
                    onChange={(e) => handleEditFormChange('containerCode', e?.target?.value)}
                    placeholder="Ex: ADT-CONT-2026-0822"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {editFormErrors?.containerCode && (
                    <p className="text-red-600 text-sm mt-1">{editFormErrors?.containerCode}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Compagnie maritime
                  </label>
                  <input
                    type="text"
                    value={editFormData?.shippingLine}
                    onChange={(e) => handleEditFormChange('shippingLine', e?.target?.value)}
                    placeholder="Ex: MSC, CMA CGM..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom du navire
                  </label>
                  <input
                    type="text"
                    value={editFormData?.vesselName}
                    onChange={(e) => handleEditFormChange('vesselName', e?.target?.value)}
                    placeholder="Ex: MSC DAKAR"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Statut
                  </label>
                  <select
                    value={editFormData?.status}
                    onChange={(e) => handleEditFormChange('status', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="AU_PORT_LE_HAVRE_ANVERS">Au port Le Havre / Anvers</option>
                    <option value="EN_TRANSIT_MARITIME">En transit maritime</option>
                    <option value="ARRIVE_A_ABIDJAN">Arrivé à Abidjan</option>
                    <option value="DEDOUANEMENT_EN_COURS">Dédouanement en cours</option>
                    <option value="TRANSFERT_A_ENTREPOT">Transfert à entrepôt</option>
                    <option value="DISTRIBUTION_EN_COURS">Distribution en cours</option>
                    <option value="CONTENEUR_TERMINE">Conteneur terminé</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Port de départ <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData?.departurePort}
                    onChange={(e) => handleEditFormChange('departurePort', e?.target?.value)}
                    placeholder="Ex: LE HAVRE"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {editFormErrors?.departurePort && (
                    <p className="text-red-600 text-sm mt-1">{editFormErrors?.departurePort}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Port d'arrivée <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData?.destinationPort}
                    onChange={(e) => handleEditFormChange('destinationPort', e?.target?.value)}
                    placeholder="Ex: ABIDJAN"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {editFormErrors?.destinationPort && (
                    <p className="text-red-600 text-sm mt-1">{editFormErrors?.destinationPort}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Date de départ <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    value={editFormData?.departureDate}
                    onChange={(e) => handleEditFormChange('departureDate', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {editFormErrors?.departureDate && (
                    <p className="text-red-600 text-sm mt-1">{editFormErrors?.departureDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Date d'arrivée estimée (ETA)
                  </label>
                  <input
                    type="date"
                    value={editFormData?.etaDate}
                    onChange={(e) => handleEditFormChange('etaDate', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notes
                </label>
                <textarea
                  value={editFormData?.notes}
                  onChange={(e) => handleEditFormChange('notes', e?.target?.value)}
                  rows={3}
                  placeholder="Informations complémentaires..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSavingEdit}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] disabled:opacity-50"
                >
                  {isSavingEdit ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}